/* eslint-disable import/no-cycle */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Anchor,
  Badge,
  Button,
  ColorSwatch,
  Loader,
  NumberFormatter,
  NumberInput,
  Skeleton,
  TextInput
} from '@mantine/core';
import { AlertCircle, Receipt, Ticket, Trash, X } from 'tabler-icons-react';
import { Link } from 'react-router-dom';
import roundHalfEven from 'round-half-even';
import dayjs from 'dayjs';
import NumberFormat from 'react-number-format';
import AppCard from '../../../../../common/AppCard';
import AppStack from '../../../../../common/AppStack';
import AppFlexbox from '../../../../../common/AppFlexbox';
import AppText from '../../../../../common/AppText';
import AppImage from '../../../../../common/AppImage';
import imageNotAvailablePlaceholder from '../../../../../../images/image-not-available.png';
import { useMediaQueries } from '../../../../../../helpers/hooks';
import CheckoutLineItemAddonsDisplay from '../../../../checkout/CheckoutLineItemAddonsDisplay';
import { formatUtcDate } from '../../../../../../helpers/format';
import ActionableIcon from '../../../../../common/ActionableIcon';
import { PAYMENT_PROVIDER_LINE_ITEM_TYPES } from '../../../../../../config/constants';
import OrderProductsTaxInfo from '../../../../orders/OrderProductsTaxInfo';

const WsCheckoutLineItem = ({
  lineItem,
  isOrderHistory,
  submitting,
  titleOverride,
  subTitleContent,
  onUpdate,
  onCustomize,
  onRedeemCoupon,
  onRemove,
  onRemoveUnavailableProducts,
  onViewPayments,
  enableImageNotAvailablePlaceholder,
  quantityLeft,
  isSingleUnit,
  expiryDate,
  isInvite,
  isProductUnavailable,
  hasProductChanged,
  onNavigate,
  productUrl,
  showFooter,
  footerTitle,
  footerSubTitle,
  footerButton,
  withBorder
}) => {
  const [debouncedQuantity, setDebouncedQuantity] = useState(null);
  const [quantity, setQuantity] = useState(null);
  const [couponCodeState, setCouponCodeState] = useState({
    code: '',
    isRemoveLoading: false,
    isLoading: false
  });
  const { isTabletOrSmaller } = useMediaQueries();

  const previewImage = enableImageNotAvailablePlaceholder
    ? lineItem?.variantImage || imageNotAvailablePlaceholder
    : lineItem?.previewImage;

  const outOfStock = quantityLeft != null && quantityLeft <= 0;
  const tooManyInCart =
    quantityLeft != null && quantityLeft - lineItem.quantity < 0;

  const expiryDateFormatted = expiryDate ? formatUtcDate(expiryDate) : null;
  let expiryTimeHours = expiryDateFormatted
    ? dayjs(expiryDateFormatted).diff(dayjs(new Date()), 'hour')
    : 0;
  const expiryTimeMinutes = expiryDateFormatted
    ? Math.ceil(
        dayjs(expiryDateFormatted).diff(dayjs(new Date()), 'minute', true)
      )
    : 0;
  const expiryTimeDays = Math.ceil(expiryTimeHours / 24);
  expiryTimeHours -= Math.floor(expiryTimeHours / 24) * 24;
  const hasExpired =
    !!expiryDateFormatted && new Date() > new Date(expiryDateFormatted);

  useEffect(() => {
    if (lineItem) {
      setQuantity(lineItem.quantity);
      setCouponCodeState({
        code: '',
        isRemoveLoading: false,
        isLoading: false
      });
    }
  }, [lineItem]);

  useEffect(() => {
    if (debouncedQuantity !== null) {
      if (debouncedQuantity !== lineItem.quantity) {
        onUpdate({ count: debouncedQuantity });
      }
    }
  }, [debouncedQuantity]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuantity(quantity);
    }, 2000);

    return () => {
      clearTimeout(handler);
    };
  }, [quantity]);

  return lineItem ? (
    <AppCard
      shadow="xs"
      style={{ padding: 0, borderRadius: isTabletOrSmaller ? 0 : 8 }}
      withBorder={withBorder}
    >
      {!isOrderHistory &&
        (hasProductChanged ? (
          <AppFlexbox
            style={{
              padding: 10,
              backgroundColor: isProductUnavailable ? '#c40000' : '#eca70a',
              color: '#FFF',
              alignItems: 'center',
              gap: 8
            }}
          >
            <AlertCircle size={18} />{' '}
            {isProductUnavailable ? (
              <AppText style={{ fontSize: 14 }}>
                This{' '}
                {lineItem.lineItemType ===
                PAYMENT_PROVIDER_LINE_ITEM_TYPES.registration.value
                  ? 'registration'
                  : 'product'}{' '}
                is no longer available
              </AppText>
            ) : (
              <AppText style={{ fontSize: 14 }}>
                This{' '}
                {lineItem.lineItemType ===
                PAYMENT_PROVIDER_LINE_ITEM_TYPES.registration.value
                  ? 'registration'
                  : 'product'}{' '}
                has been changed
              </AppText>
            )}
            {!submitting && (
              <Anchor
                onClick={onRemoveUnavailableProducts}
                style={{ marginLeft: 'auto', color: '#FFF', fontSize: 14 }}
                underline="always"
              >
                Update cart
              </Anchor>
            )}
          </AppFlexbox>
        ) : hasExpired ? (
          <AppFlexbox
            style={{
              padding: 10,
              backgroundColor: '#c40000',
              color: '#FFF',
              alignItems: 'center',
              gap: 8
            }}
          >
            <AlertCircle size={18} />{' '}
            <AppText>Registration has expired</AppText>
            {!submitting && (
              <Anchor
                onClick={onRemoveUnavailableProducts}
                style={{ marginLeft: 'auto', color: '#FFF', fontSize: 14 }}
                underline="always"
              >
                Update cart
              </Anchor>
            )}
          </AppFlexbox>
        ) : expiryDateFormatted && expiryTimeDays < 30 ? (
          <AppFlexbox
            style={{
              padding: 10,
              alignItems: 'center',
              borderBottom: '1px solid #dee2e6',
              gap: 8
            }}
          >
            <ColorSwatch color="#eca70a" size={18} />
            <AppText>
              Registration {isInvite ? 'expires' : 'closes'} in{' '}
              {expiryTimeDays > 1 ? (
                <b>
                  {expiryTimeDays} {expiryTimeDays === 1 ? 'day' : 'days'}{' '}
                </b>
              ) : expiryTimeHours > 0 ? (
                <b>
                  {expiryTimeHours} {expiryTimeHours === 1 ? 'hour' : 'hours'}{' '}
                </b>
              ) : (
                expiryTimeMinutes > 0 && (
                  <b>
                    {expiryTimeMinutes}{' '}
                    {expiryTimeMinutes === 1 ? 'minute' : 'minutes'}{' '}
                  </b>
                )
              )}
            </AppText>
          </AppFlexbox>
        ) : (
          quantityLeft != null &&
          (outOfStock ? (
            <AppFlexbox
              style={{
                padding: 10,
                alignItems: 'center',
                borderBottom: '1px solid #dee2e6',
                gap: 8
              }}
            >
              <ColorSwatch color="#c40000" size={16} />
              <AppText style={{ fontSize: 14 }}>Out of stock</AppText>
              {!submitting && (
                <Anchor
                  onClick={onRemoveUnavailableProducts}
                  style={{ marginLeft: 'auto', fontSize: 14 }}
                  underline="always"
                >
                  Update cart
                </Anchor>
              )}
            </AppFlexbox>
          ) : tooManyInCart ? (
            <AppFlexbox
              style={{
                padding: 10,
                alignItems: 'center',
                borderBottom: '1px solid #dee2e6',
                gap: 8
              }}
            >
              <ColorSwatch color="#c40000" size={18} />
              <AppText style={{ fontSize: 14 }}>
                Only {quantityLeft} left. Please remove some from your cart.
              </AppText>
              {!submitting && (
                <Anchor
                  onClick={onRemoveUnavailableProducts}
                  style={{ marginLeft: 'auto', fontSize: 14 }}
                  underline="always"
                >
                  Update cart
                </Anchor>
              )}
            </AppFlexbox>
          ) : (
            quantityLeft <= 25 && (
              <AppFlexbox
                style={{
                  padding: 10,
                  alignItems: 'center',
                  borderBottom: '1px solid #dee2e6',
                  gap: 8
                }}
              >
                <ColorSwatch color="#eca70a" size={18} />
                <AppText style={{ fontSize: 14 }}>
                  Low stock: {quantityLeft} left
                </AppText>
              </AppFlexbox>
            )
          ))
        ))}
      <AppStack style={{ gap: 0, flexWrap: 'nowrap' }}>
        <AppFlexbox
          style={{
            flex: 1,
            margin: 16,
            flexWrap: 'nowrap',
            flexDirection: isTabletOrSmaller ? 'column' : 'row'
          }}
        >
          <AppFlexbox
            style={{ flexWrap: 'nowrap', alignSelf: 'stretch', flex: 2 }}
          >
            <AppFlexbox style={{ alignSelf: 'start', flex: 1 }}>
              <AppFlexbox component={Link} onClick={onNavigate} to={productUrl}>
                <AppImage
                  fit="contain"
                  height={isTabletOrSmaller ? 70 : 100}
                  src={previewImage}
                  width={isTabletOrSmaller ? 70 : 100}
                />
              </AppFlexbox>

              <AppStack
                style={{
                  flex: 1,
                  gap: 20,
                  justifyContent: 'center'
                }}
              >
                <AppStack
                  style={{
                    flex: 1,
                    gap: 0
                  }}
                >
                  <AppStack
                    component={Link}
                    onClick={onNavigate}
                    style={{
                      gap: 0,
                      textDecoration: 'none',
                      color: '#000'
                    }}
                    to={productUrl}
                  >
                    <AppText style={{ fontSize: 18, fontWeight: 700 }}>
                      {titleOverride || lineItem.productName}
                    </AppText>

                    {subTitleContent}

                    <CheckoutLineItemAddonsDisplay addOns={lineItem.addOns} />

                    {!isSingleUnit && isOrderHistory && (
                      <AppText style={{ fontSize: 12, color: '#666' }}>
                        Qty: {lineItem.quantity}
                      </AppText>
                    )}
                  </AppStack>
                </AppStack>
                {!isOrderHistory && !isSingleUnit && (
                  <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
                    <NumberInput
                      allowNegative={false}
                      disabled={outOfStock || submitting || !!hasProductChanged}
                      min={1}
                      onChange={(v) => {
                        let newQuantity =
                          quantityLeft !== null && v > quantityLeft
                            ? quantityLeft
                            : v;
                        if (newQuantity <= 0) {
                          newQuantity = 1;
                        }
                        setQuantity(newQuantity);
                      }}
                      size="xs"
                      style={{ flex: 1, maxWidth: 80 }}
                      value={quantity}
                    />
                    {!hasProductChanged && lineItem.isCustomizable && (
                      <Anchor
                        onClick={onCustomize}
                        style={{ fontSize: 14, color: 'dodgerblue' }}
                        underline="always"
                      >
                        Customize
                      </Anchor>
                    )}
                  </AppFlexbox>
                )}
              </AppStack>
            </AppFlexbox>
          </AppFlexbox>

          <AppStack
            style={{
              flex: 1,
              alignSelf: 'start',
              placeItems: 'end',
              marginLeft: 'auto',
              gap: 10
            }}
          >
            <AppStack style={{ gap: 0, alignItems: 'end' }}>
              <AppText
                style={{
                  fontSize: 18,
                  textDecoration: lineItem.financialAssistance
                    ? 'line-through'
                    : 'unset'
                }}
                weight={700}
              >
                <NumberFormatter
                  decimalScale={2}
                  displayType="text"
                  fixedDecimalScale
                  prefix="$"
                  suffix={` ${lineItem.currency?.toUpperCase()}`}
                  thousandSeparator
                  value={(lineItem.subtotalInCents * lineItem.quantity) / 100}
                />
              </AppText>

              {!isSingleUnit && (
                <AppText style={{ fontSize: 14, color: '#666' }}>
                  <NumberFormatter
                    decimalScale={2}
                    displayType="text"
                    fixedDecimalScale
                    prefix="$"
                    suffix={` each`}
                    thousandSeparator
                    value={lineItem.subtotalInCents / 100}
                  />
                </AppText>
              )}

              {lineItem.coupons.length > 0 && (
                <AppStack style={{ gap: 5 }}>
                  {lineItem.coupons.map((c) => (
                    <AppFlexbox
                      key={`${c.couponName}-${c.unitAmountInCents}`}
                      style={{
                        gap: 5,
                        flexWrap: 'nowrap',
                        alignItems: 'center'
                      }}
                    >
                      <Badge
                        color="gray"
                        leftSection={<Ticket size={16} />}
                        rightSection={
                          isOrderHistory ||
                          c.isGroupCoupon ? null : couponCodeState.isRemoveLoading ? (
                            <Loader color="dark" size={14} />
                          ) : (
                            <ActionableIcon
                              color="gray"
                              onClick={() => {
                                setCouponCodeState({
                                  code: '',
                                  isRemoveLoading: true,
                                  isLoading: false
                                });
                                onRedeemCoupon();
                              }}
                              size="xs"
                              variant="transparent"
                            >
                              <X size={15} />
                            </ActionableIcon>
                          )
                        }
                        variant="outline"
                      >
                        {c.couponName}
                      </Badge>
                      <AppText size="md" weight={500}>
                        <NumberFormat
                          decimalScale={2}
                          displayType="text"
                          fixedDecimalScale
                          prefix="$"
                          thousandSeparator
                          value={
                            c.unitAmountInCents
                              ? (c.unitAmountInCents / 100) * -1
                              : ((lineItem.subtotalInCents *
                                  (c.percentageAmount / 100)) /
                                  100) *
                                -1
                          }
                        />{' '}
                        {lineItem.currency?.toUpperCase()}
                      </AppText>
                    </AppFlexbox>
                  ))}
                </AppStack>
              )}

              {lineItem.tax.length > 0 && (
                <OrderProductsTaxInfo
                  taxItems={[
                    {
                      entityId: lineItem.entityId,
                      tax: lineItem.tax.map((t) => ({
                        name: t.name,
                        taxRate: t.taxRate,
                        taxAmount:
                          roundHalfEven(
                            (lineItem.subtotalInCents * t.taxRate) / 100,
                            0
                          ) * lineItem.quantity
                      }))
                    }
                  ]}
                  textStyle={{
                    textDecoration: lineItem.financialAssistance
                      ? 'line-through'
                      : 'unset'
                  }}
                />
              )}

              {lineItem.customerPaysServiceFees &&
                lineItem.serviceFeeInCents > 0 && (
                  <AppFlexbox style={{ gap: 5, flexWrap: 'nowrap' }}>
                    <AppText
                      style={{
                        textDecoration: lineItem.financialAssistance
                          ? 'line-through'
                          : 'unset'
                      }}
                      weight={500}
                    >
                      <NumberFormatter
                        decimalScale={2}
                        displayType="text"
                        fixedDecimalScale
                        prefix="+ $"
                        suffix=" Service fee"
                        thousandSeparator
                        value={lineItem.serviceFeeInCents / 100}
                      />
                    </AppText>
                  </AppFlexbox>
                )}

              {lineItem.financialAssistance && (
                <AppFlexbox style={{ gap: 5, flexWrap: 'nowrap' }}>
                  <AppText size="md" weight={500}>
                    Jumpstart{' '}
                    <NumberFormat
                      decimalScale={2}
                      displayType="text"
                      fixedDecimalScale
                      prefix="$"
                      thousandSeparator
                      value={0.0}
                    />{' '}
                    {lineItem.currency?.toUpperCase()}
                  </AppText>
                </AppFlexbox>
              )}
            </AppStack>
            {!isOrderHistory ? (
              <AppStack style={{ gap: 20, flex: 1 }}>
                <AppFlexbox
                  onClick={() => {
                    if (!submitting) {
                      onRemove(lineItem.entityId);
                    }
                  }}
                  style={{
                    cursor: submitting ? 'default' : 'pointer',
                    gap: 5,
                    alignSelf: 'end',
                    alignItems: 'center',
                    color: submitting ? '#999' : '#c40000'
                  }}
                >
                  <Trash size={18} />
                  <AppText style={{ fontSize: 14 }}>Remove</AppText>
                </AppFlexbox>

                {onRedeemCoupon && lineItem.subtotalInCents > 0 && (
                  <AppStack
                    component="form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      setCouponCodeState({
                        ...couponCodeState,
                        isLoading: true
                      });
                      onRedeemCoupon(couponCodeState.code);
                    }}
                    style={{ gap: 10, flex: 1 }}
                  >
                    <TextInput
                      disabled={submitting}
                      onChange={(e) =>
                        setCouponCodeState({
                          ...couponCodeState,
                          code: e.currentTarget.value
                            .toUpperCase()
                            .replace(' ', '')
                        })
                      }
                      placeholder="Enter Coupon Code"
                      style={{ flex: 1, maxWidth: 200 }}
                      value={couponCodeState.code}
                    />
                    {couponCodeState.code && (
                      <Button
                        color="blue"
                        loading={couponCodeState.isLoading}
                        style={{ flex: 1, maxWidth: 200 }}
                        styles={{ inner: { height: 35 } }}
                        type="submit"
                      >
                        Apply
                      </Button>
                    )}
                  </AppStack>
                )}
              </AppStack>
            ) : (
              onViewPayments && (
                <AppFlexbox
                  onClick={() => onViewPayments()}
                  style={{ cursor: 'pointer', gap: 10 }}
                >
                  <Receipt />
                  <AppText>View Payments</AppText>
                </AppFlexbox>
              )
            )}
          </AppStack>
        </AppFlexbox>

        {showFooter && (
          <AppFlexbox
            style={{
              backgroundColor: '#000',
              color: '#FFF',
              opacity: 0.8,
              padding: 16,
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <AppStack style={{ gap: 0 }}>
              <AppText style={{ fontSize: 16, fontWeight: 700 }}>
                {footerTitle}
              </AppText>
              {footerSubTitle && (
                <AppText style={{ fontSize: 16 }}>{footerSubTitle}</AppText>
              )}
            </AppStack>

            {!isOrderHistory && hasExpired ? (
              <Button
                color="gray"
                disabled
                radius="xs"
                size="lg"
                style={{ width: 200 }}
                variant="filled"
              >
                Expired
              </Button>
            ) : (
              footerButton && footerButton
            )}
          </AppFlexbox>
        )}
      </AppStack>
    </AppCard>
  ) : (
    <AppCard
      shadow="xs"
      style={{ padding: 0, borderRadius: isTabletOrSmaller ? 0 : 8 }}
      withBorder={withBorder}
    >
      <AppStack style={{ gap: 0, flexWrap: 'nowrap' }}>
        <AppFlexbox
          style={{
            flex: 1,
            margin: 16,
            flexWrap: 'nowrap',
            flexDirection: isTabletOrSmaller ? 'column' : 'row'
          }}
        >
          <AppFlexbox
            style={{ flexWrap: 'nowrap', alignSelf: 'stretch', flex: 2 }}
          >
            <AppFlexbox style={{ alignSelf: 'start', flex: 1 }}>
              <Skeleton height={100} width={100} />

              <AppStack
                style={{
                  flex: 1,
                  gap: 20,
                  justifyContent: 'center'
                }}
              >
                <AppStack
                  style={{
                    flex: 1,
                    gap: 10
                  }}
                >
                  <Skeleton height={16} width="50%" />
                  <Skeleton height={14} width="40%" />
                </AppStack>
              </AppStack>
            </AppFlexbox>
          </AppFlexbox>

          <AppStack
            style={{
              flex: 1,
              alignSelf: 'start',
              placeItems: 'end',
              marginLeft: 'auto',
              gap: 10
            }}
          >
            <AppStack style={{ gap: 10, alignItems: 'end' }}>
              <Skeleton height={20} width={100} />

              <Skeleton height={15} width={80} />
            </AppStack>
          </AppStack>
        </AppFlexbox>

        {showFooter && (
          <AppFlexbox
            style={{
              backgroundColor: '#000',
              color: '#FFF',
              opacity: 0.8,
              padding: 16,
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <AppStack style={{ gap: 10 }}>
              <Skeleton height={10} width={100} />
              <Skeleton height={10} width={100} />
            </AppStack>

            {footerButton && <Skeleton height={48} width={200} />}
          </AppFlexbox>
        )}
      </AppStack>
    </AppCard>
  );
};

WsCheckoutLineItem.propTypes = {
  enableImageNotAvailablePlaceholder: PropTypes.bool,
  expiryDate: PropTypes.string,
  footerBottom: PropTypes.node,
  footerButton: PropTypes.node,
  footerSubTitle: PropTypes.string,
  footerTitle: PropTypes.string,
  hasProductChanged: PropTypes.bool,
  isInvite: PropTypes.bool,
  isOrderHistory: PropTypes.bool,
  isProductUnavailable: PropTypes.bool,
  isSingleUnit: PropTypes.bool,
  lineItem: PropTypes.object,
  onCustomize: PropTypes.func,
  onNavigate: PropTypes.func,
  onRedeemCoupon: PropTypes.func,
  onRemove: PropTypes.func,
  onRemoveUnavailableProducts: PropTypes.func,
  onUpdate: PropTypes.func,
  onViewPayments: PropTypes.func,
  productUrl: PropTypes.string,
  quantityLeft: PropTypes.number,
  showFooter: PropTypes.bool,
  subTitleContent: PropTypes.node,
  submitting: PropTypes.bool,
  titleOverride: PropTypes.string,
  withBorder: PropTypes.bool
};

export default WsCheckoutLineItem;
